import { DataItem } from 'scrivito'

import { ActivityType } from '../../const'
import { formatDateKey } from '../../utils/dateUtils'
import { ensureString } from '@/utils/ensureString'

export interface RecordsAggregate {
  dailyHours: Map<string, number>
  dailyRecordIds: Map<string, string[]>
  dailyBillableStatus: Map<string, boolean>
  dailyBillableHours: Map<string, number>
  dailyNonBillableHours: Map<string, number>
  totalHours: number
  billableHours: number
  nonBillableHours: number
  supportHours: number
  managedTeamHours: number
  hasNonBillable: boolean
}

const readRecord = (record: DataItem) => {
  const totalDuration = Number(record.get('totalDuration')) || 0
  const billedDuration = Number(record.get('billedDuration')) || 0
  const isBillable = record.get('billable') === true
  return {
    id: record.id(),
    date: formatDateKey(new Date(ensureString(record.get('recordedAt')))),
    totalDuration,
    billedDuration,
    isBillable,
    activityType: ensureString(record.get('activityType')),
    billableHours: isBillable ? billedDuration : 0,
    nonBillableHours: isBillable
      ? Math.max(0, totalDuration - billedDuration)
      : totalDuration,
  }
}

const addToNumberMap = (map: Map<string, number>, key: string, value: number) =>
  map.set(key, (map.get(key) || 0) + value)

const emptyAggregate = (): RecordsAggregate => ({
  dailyHours: new Map(),
  dailyRecordIds: new Map(),
  dailyBillableStatus: new Map(),
  dailyBillableHours: new Map(),
  dailyNonBillableHours: new Map(),
  totalHours: 0,
  billableHours: 0,
  nonBillableHours: 0,
  supportHours: 0,
  managedTeamHours: 0,
  hasNonBillable: false,
})

export const accumulateRecords = (records: DataItem[]): RecordsAggregate =>
  records.reduce<RecordsAggregate>((acc, record) => {
    const r = readRecord(record)

    addToNumberMap(acc.dailyHours, r.date, r.totalDuration)
    addToNumberMap(acc.dailyBillableHours, r.date, r.billableHours)
    addToNumberMap(acc.dailyNonBillableHours, r.date, r.nonBillableHours)

    const ids = acc.dailyRecordIds.get(r.date) ?? []
    ids.push(r.id)
    acc.dailyRecordIds.set(r.date, ids)

    acc.totalHours += r.totalDuration
    acc.billableHours += r.billableHours
    acc.nonBillableHours += r.nonBillableHours

    if (r.activityType === ActivityType.SUPPORT) {
      acc.supportHours += r.totalDuration
    }
    if (r.activityType === ActivityType.MANAGED_TEAM) {
      acc.managedTeamHours += r.totalDuration
    }

    if (!r.isBillable) {
      acc.hasNonBillable = true
      acc.dailyBillableStatus.set(r.date, false)
    } else if (!acc.dailyBillableStatus.has(r.date)) {
      acc.dailyBillableStatus.set(r.date, true)
    }

    return acc
  }, emptyAggregate())
